import { Login } from 'src/app/shared/model/login';
import { LoginService } from 'src/app/shared/services/login.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UserConstant } from 'src/app/shared/constants/user-constant';
import { AuthService } from 'src/app/shared/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginAclDeniedModalComponent } from './login-acl-denied-modal/login-acl-denied-modal.component';

export enum LoginUiState {
  LOGIN = 'LOGIN',
  LOGIN_LOADING = 'LOGIN_LOADING',
  OTP_REQUIRED = 'OTP_REQUIRED',
  OTP_VERIFYING = 'OTP_VERIFYING',
  OTP_INVALID = 'OTP_INVALID',
  OTP_EXPIRED = 'OTP_EXPIRED',
  OTP_RATE_LIMITED = 'OTP_RATE_LIMITED',
  OTP_RESEND_COOLDOWN = 'OTP_RESEND_COOLDOWN',
  OTP_RESEND_SUCCESS = 'OTP_RESEND_SUCCESS',
  SERVICE_ERROR = 'SERVICE_ERROR',
  AUTHENTICATED = 'AUTHENTICATED'
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  otpForm: FormGroup;
  hide = true;
  state = LoginUiState.LOGIN;
  readonly states = LoginUiState;
  resendCooldownSeconds = 0;
  rememberDevice = false;
  trustedDeviceDays = 7;
  readonly trustedDeviceDurations = [7, 14, 30];
  private challengeId: string;
  private cooldownTimer: any;
  private returnUrl = '';

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private toastr: ToastrService,
              private loginService: LoginService,
              private authService: AuthService,
              private dialog: MatDialog,
              private route?: ActivatedRoute
  ) { }

  ngOnInit() {
    const requestedReturnUrl = this.route ? this.route.snapshot.queryParamMap.get('returnUrl') || '' : '';
    if (/^\/layout\/report\/cellular-reports(?:\/review\/[a-f0-9]{24}|\/overlay-bundles)?(?:\?|$)/i.test(requestedReturnUrl)) {
      this.returnUrl = requestedReturnUrl;
    }
    this.loginForm = this.formBuilder.group({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, Validators.minLength(6)])
    });
    this.otpForm = this.formBuilder.group({
      otp: new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)])
    });
    this.clearAuthentication();
  }

  ngOnDestroy() {
    this.stopCooldown();
    this.clearSensitiveInput();
  }

  onSubmit() {
    if (this.loginForm.invalid || this.state === LoginUiState.LOGIN_LOADING) {
      return;
    }
    this.state = LoginUiState.LOGIN_LOADING;
    this.loginService.userLogin(this.loginForm.value as Login).subscribe(res => {
      this.loginForm.controls.password.setValue('');
      if (res && res.mfaRequired === true && this.isChallengeId(res.challengeId)) {
        this.challengeId = res.challengeId;
        this.state = LoginUiState.OTP_REQUIRED;
        this.startCooldown(30);
        return;
      }
      if (res && res.token && res.user) {
        this.state = LoginUiState.AUTHENTICATED;
        this.finishAuthentication(res);
        return;
      }
      this.state = LoginUiState.SERVICE_ERROR;
    }, err => {
      this.loginForm.controls.password.setValue('');
      this.state = LoginUiState.LOGIN;
      if (this.isLoginAclDenied(err)) {
        this.openLoginAclDeniedModal(err);
        return;
      }
      if (err && (err.status === 429 || err.status === 503)) {
        this.state = LoginUiState.SERVICE_ERROR;
        this.toastr.warning(err.status === 429
          ? 'Too many attempts. Please try again later.'
          : 'Sign in is temporarily unavailable. Please try again.');
        return;
      }
      this.toastr.warning(this.getLoginErrorMessage(err));
    });
  }

  verifyOtp() {
    if (!this.challengeId || this.otpForm.invalid || this.state === LoginUiState.OTP_VERIFYING) {
      return;
    }
    this.state = LoginUiState.OTP_VERIFYING;
    const otp = this.otpForm.controls.otp.value;
    this.loginService.verifyMfa(
      this.challengeId,
      otp,
      this.rememberDevice,
      this.rememberDevice ? this.trustedDeviceDays : 7
    ).subscribe(res => {
      this.otpForm.reset();
      if (!res || !res.token || !res.user) {
        this.state = LoginUiState.SERVICE_ERROR;
        return;
      }
      this.state = LoginUiState.AUTHENTICATED;
      this.finishAuthentication(res);
    }, err => {
      this.otpForm.reset();
      this.state = this.stateForMfaError(err);
    });
  }

  resendOtp() {
    if (!this.challengeId || this.resendCooldownSeconds > 0) {
      this.state = LoginUiState.OTP_RESEND_COOLDOWN;
      return;
    }
    this.state = LoginUiState.OTP_RESEND_COOLDOWN;
    this.loginService.resendMfa(this.challengeId).subscribe(res => {
      if (!res || res.mfaRequired !== true || !this.isChallengeId(res.challengeId)) {
        this.state = LoginUiState.SERVICE_ERROR;
        return;
      }
      this.challengeId = res.challengeId;
      this.otpForm.reset();
      this.state = LoginUiState.OTP_RESEND_SUCCESS;
      this.startCooldown(30);
    }, err => {
      const retryAfter = Number(err && err.error && err.error.retryAfterSeconds);
      if (err && err.status === 429) {
        this.startCooldown(Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : 30);
      }
      this.state = this.stateForMfaError(err);
    });
  }

  normalizeOtpInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const normalized = String(input.value || '').replace(/\D/g, '').slice(0, 6);
    if (input.value !== normalized) {
      input.value = normalized;
      this.otpForm.controls.otp.setValue(normalized);
    }
    if ([LoginUiState.OTP_INVALID, LoginUiState.OTP_RESEND_SUCCESS].includes(this.state)) {
      this.state = LoginUiState.OTP_REQUIRED;
    }
  }

  returnToLogin() {
    this.challengeId = null;
    this.rememberDevice = false;
    this.trustedDeviceDays = 7;
    this.stopCooldown();
    this.clearSensitiveInput();
    this.state = LoginUiState.LOGIN;
  }

  isLoginAclDenied(err) {
    const errorBody = err && err.error ? err.error : {};
    const errorMessage = errorBody.error || errorBody.message || '';
    return ['IP_NOT_ALLOWED', 'IP_DENIED'].includes(errorBody.code)
      || err && err.status === 403 && errorMessage.toLowerCase().indexOf('access denied from this network') > -1
      || errorMessage.toLowerCase().indexOf('login acl') > -1
      || errorMessage.toLowerCase().indexOf('source ip') > -1
      || errorMessage.toLowerCase().indexOf('ip restriction') > -1;
  }

  openLoginAclDeniedModal(err) {
    const errorBody = err && err.error ? err.error : {};
    this.dialog.open(LoginAclDeniedModalComponent, {
      width: '420px',
      disableClose: false,
      data: { sourceIp: errorBody.sourceIp }
    });
  }

  getLoginErrorMessage(err) {
    if (this.isLoginAclDenied(err)) {
      return 'Login denied from this source IP';
    }
    return 'Invalid login credentials';
  }

  private finishAuthentication(res) {
    this.authService.setCookie('USER_TOKEN', res.token, 60);
    localStorage.setItem(UserConstant.TOKEN, res.token);
    localStorage.setItem(UserConstant.USER, JSON.stringify(res.user));
    localStorage.setItem(UserConstant.LOGINTYPE, 'local');
    this.challengeId = null;
    this.stopCooldown();
    this.toastr.success('Login success');
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      this.router.navigate(['/layout']);
    }
  }

  private stateForMfaError(err): LoginUiState {
    const status = err && err.error && err.error.status;
    if (status === 'EXPIRED' || status === 'ALREADY_USED' || err && err.status === 410) {
      this.challengeId = null;
      return LoginUiState.OTP_EXPIRED;
    }
    if (status === 'RATE_LIMITED' || status === 'ATTEMPTS_EXCEEDED' || err && err.status === 429) {
      return LoginUiState.OTP_RATE_LIMITED;
    }
    if (status === 'SERVICE_UNAVAILABLE' || err && err.status === 503) {
      return LoginUiState.SERVICE_ERROR;
    }
    return LoginUiState.OTP_INVALID;
  }

  private startCooldown(seconds: number) {
    this.stopCooldown();
    this.resendCooldownSeconds = seconds;
    this.cooldownTimer = setInterval(() => {
      this.resendCooldownSeconds = Math.max(0, this.resendCooldownSeconds - 1);
      if (this.resendCooldownSeconds === 0) {
        this.stopCooldown();
        if (this.state === LoginUiState.OTP_RESEND_COOLDOWN) {
          this.state = LoginUiState.OTP_REQUIRED;
        }
      }
    }, 1000);
  }

  private stopCooldown() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this.resendCooldownSeconds = 0;
  }

  private clearSensitiveInput() {
    if (this.loginForm) this.loginForm.controls.password.setValue('');
    if (this.otpForm) this.otpForm.reset();
  }

  private clearAuthentication() {
    this.authService.clearCookie('USER_TOKEN');
    localStorage.removeItem(UserConstant.TOKEN);
    localStorage.removeItem(UserConstant.USER);
    localStorage.removeItem(UserConstant.LOGINTYPE);
    localStorage.removeItem(UserConstant.PASSWORDTOKEN);
  }

  private isChallengeId(value: string): boolean {
    return /^[a-f0-9]{64}$/.test(String(value || ''));
  }
}
